// apps/flux-ems-web/src/components/simulation/LiveProgressView.jsx
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, Chip, LinearProgress, Button } from '@mui/material';
import { Loop as LoopIcon, Cancel as CancelIcon } from '@mui/icons-material';
import {
    BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import { ConsoleLine } from '@vortx/shared-ui';
import AgentNetworkDiagram from './AgentNetworkDiagram';

const GOLD = '#b28c50';
const GOLD_LIGHT = '#d4a574';
const GOLD_GRADIENT = 'linear-gradient(135deg, #b28c50 0%, #d4a574 100%)';
const HEADING_FONT = "'Syne', sans-serif";
const MONO_FONT = "'JetBrains Mono', monospace";

const PHASE_LABELS = {
    initializing: 'INIT',
    weather: 'WEATHER',
    tariff: 'TARIFF',
    siloed: 'SILOED',
    orchestrated: 'ORCHESTRATED',
};

const AGENT_LABELS_DEFAULT = {
    battery: 'BATT',
    site_dna: 'SITE',
    ev_fleet: 'EV',
    cold_storage: 'COLD',
    sitedna: 'SITE',
    evflee: 'EV',
    coldst: 'COLD',
};

/**
 * Live progress view shown while simulation is running.
 * Features a retro SCADA-style console for real-time agent activity.
 */
export default function LiveProgressView({ run, agents, themeColors, onCancel }) {
    const { isRetro, CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = themeColors;
    const consoleRef = useRef(null);

    const progress = run?.progress || {};
    const phase = progress.phase || 'initializing';
    const monthlyResults = progress.monthly_results || {};
    const activityLog = progress.activity_log || [];
    const serverDay = progress.day || 0;
    const totalDays = progress.total_days || 365;
    const agentStates = progress.agents || {};
    const dailyStats = progress.daily_stats || {};
    const agentLabels = progress.agent_labels || {};
    const gridPeakKw = progress.grid_peak_kw || 0;
    const gridEnergyKwh = progress.grid_energy_kwh || 0;
    const step = progress.step || 0;
    const totalSteps = progress.total_steps || 0;

    // Animate day counter — tick from previous value to serverDay
    const [displayDay, setDisplayDay] = useState(0);
    const prevDayRef = useRef(0);

    useEffect(() => {
        if (serverDay <= prevDayRef.current) {
            // Phase reset or no change
            if (serverDay < prevDayRef.current) {
                prevDayRef.current = 0;
                setDisplayDay(0);
            }
            return;
        }
        const startDay = prevDayRef.current;
        const endDay = serverDay;
        const steps = endDay - startDay;
        if (steps <= 0) return;

        // Spread the ticking over ~3 seconds (typical poll interval)
        const interval = Math.max(40, Math.min(200, 3000 / steps));
        let current = startDay;
        const timer = setInterval(() => {
            current++;
            setDisplayDay(current);
            if (current >= endDay) {
                clearInterval(timer);
                prevDayRef.current = endDay;
            }
        }, interval);
        return () => clearInterval(timer);
    }, [serverDay]);

    const day = displayDay;

    // Auto-scroll console to bottom
    useEffect(() => {
        if (consoleRef.current) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
        }
    }, [activityLog.length, progress.message]);

    // Derive cumulative data for chart
    const { cumulativeData, barData, monthlyFeed, totals } = useMemo(() => {
        const siloedMonths = monthlyResults.siloed || {};
        const orchMonths = monthlyResults.orchestrated || {};

        const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

        let siloedCum = 0;
        let orchCum = 0;
        const cumData = [];
        const bars = [];
        const feed = [];

        for (let m = 0; m < 12; m++) {
            const sm = siloedMonths[m];
            const om = orchMonths[m];

            if (sm) {
                siloedCum += sm.total_cost;
                cumData.push({
                    month: MONTH_NAMES[m],
                    siloed: Math.round(siloedCum),
                    orchestrated: om ? Math.round(orchCum + (om.total_cost || 0)) : null,
                });
                bars.push({
                    month: MONTH_NAMES[m],
                    siloed: Math.round(sm.total_cost),
                    orchestrated: om ? Math.round(om.total_cost) : null,
                });
            }
            if (om) {
                orchCum += om.total_cost;
                if (!sm) {
                    cumData.push({
                        month: MONTH_NAMES[m],
                        siloed: Math.round(siloedCum),
                        orchestrated: Math.round(orchCum),
                    });
                } else {
                    const last = cumData[cumData.length - 1];
                    if (last) last.orchestrated = Math.round(orchCum);
                }

                const saved = (sm ? sm.total_cost : 0) - om.total_cost;
                feed.push(
                    `[${MONTH_NAMES[m]}] Peak: ${om.peak_kw} kW (orchestrated)`,
                    `[${MONTH_NAMES[m]}] Savings: $${Math.round(saved).toLocaleString()}`,
                );
            }
        }

        return {
            cumulativeData: cumData,
            barData: bars,
            monthlyFeed: feed,
            totals: {
                siloedCost: Math.round(siloedCum),
                orchCost: Math.round(orchCum),
                savings: Math.round(siloedCum - orchCum),
                pct: siloedCum > 0 ? ((siloedCum - orchCum) / siloedCum * 100).toFixed(1) : 0,
            },
        };
    }, [monthlyResults]);

    const networkPhase = phase === 'siloed' ? 'siloed'
        : phase === 'orchestrated' ? 'orchestrated'
        : 'initializing';

    const orchMonthCount = Object.keys(monthlyResults.orchestrated || {}).length;
    const coordIntensity = orchMonthCount > 0 ? Math.min(1, orchMonthCount / 12) : 0;

    // Progress percentage for determinate bar
    const progressPct = day > 0
        ? Math.round((day / totalDays) * 100)
        : totalSteps > 0
            ? Math.round((step / totalSteps) * 100)
            : 0;

    return (
        <Box sx={{ mb: 4 }}>
            {/* Phase indicator + cancel */}
            <Paper sx={{ p: 2, mb: 3, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT }}>
                        Simulation Running
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                            label={progress.message || phase}
                            size="small"
                            icon={<LoopIcon />}
                            sx={{ color: GOLD, borderColor: GOLD, fontFamily: MONO_FONT }}
                            variant="outlined"
                        />
                        {onCancel && (
                            <Button
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={onCancel}
                                sx={{ color: '#ff6b6b', borderColor: '#ff6b6b', fontFamily: MONO_FONT, fontSize: '0.75rem' }}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                </Box>
                <LinearProgress
                    variant={progressPct > 0 ? 'determinate' : 'indeterminate'}
                    value={progressPct}
                    sx={{
                        height: 4, borderRadius: 2,
                        backgroundColor: 'rgba(178,140,80,0.1)',
                        '& .MuiLinearProgress-bar': { background: GOLD_GRADIENT, borderRadius: 2 },
                    }}
                />
            </Paper>

            <Grid container spacing={3}>
                {/* Left column: console + metrics + charts */}
                <Grid item xs={12} md={9}>

                    {/* === RETRO CONSOLE === */}
                    <Paper sx={{
                        mb: 3, overflow: 'hidden',
                        background: 'linear-gradient(180deg, #0a0e1a 0%, #0d1117 100%)',
                        border: '1px solid rgba(178, 140, 80, 0.3)',
                        borderRadius: 1,
                    }}>
                        {/* Console header bar */}
                        <Box sx={{
                            px: 2, py: 1.5,
                            background: 'linear-gradient(90deg, rgba(178,140,80,0.12) 0%, transparent 100%)',
                            borderBottom: '1px solid rgba(178,140,80,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: GOLD,
                                    boxShadow: `0 0 6px ${GOLD}`,
                                    '@keyframes consolePulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.4 },
                                    },
                                    animation: 'consolePulse 2s ease-in-out infinite',
                                }} />
                                <Typography sx={{
                                    color: GOLD, fontFamily: MONO_FONT, fontSize: '0.8rem',
                                    fontWeight: 700, letterSpacing: '0.1em',
                                }}>
                                    VORTX SIMULATION CONSOLE
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {day > 0 && (
                                    <Typography sx={{
                                        color: 'rgba(244,241,236,0.5)', fontFamily: MONO_FONT, fontSize: '0.7rem',
                                    }}>
                                        DAY {day}/{totalDays}
                                    </Typography>
                                )}
                                <Box sx={{
                                    px: 1.5, py: 0.3, borderRadius: 0.5,
                                    border: `1px solid ${GOLD}`,
                                    background: 'rgba(178,140,80,0.1)',
                                }}>
                                    <Typography sx={{
                                        color: GOLD, fontFamily: MONO_FONT, fontSize: '0.65rem',
                                        fontWeight: 700, letterSpacing: '0.08em',
                                    }}>
                                        {PHASE_LABELS[phase] || phase.toUpperCase()}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Day progress bar (determinate when we have day data) */}
                        {day > 0 && (
                            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(178,140,80,0.1)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography sx={{
                                        color: 'rgba(244,241,236,0.4)', fontFamily: MONO_FONT, fontSize: '0.65rem',
                                    }}>
                                        SIMULATION PROGRESS
                                    </Typography>
                                    <Typography sx={{
                                        color: GOLD_LIGHT, fontFamily: MONO_FONT, fontSize: '0.65rem',
                                    }}>
                                        {progressPct}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPct}
                                    sx={{
                                        height: 3, borderRadius: 1,
                                        backgroundColor: 'rgba(178,140,80,0.08)',
                                        '& .MuiLinearProgress-bar': {
                                            background: GOLD_GRADIENT, borderRadius: 1,
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        {/* Agent status bar — daily peak + energy from daily_stats */}
                        {Object.keys(agentStates).length > 0 && (
                            <Box sx={{
                                px: 2, py: 1,
                                borderBottom: '1px solid rgba(178,140,80,0.1)',
                                display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center',
                            }}>
                                {/* Grid total */}
                                {gridPeakKw > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{
                                            width: 6, height: 6, borderRadius: '50%',
                                            background: GOLD,
                                            boxShadow: `0 0 4px ${GOLD}`,
                                        }} />
                                        <Typography sx={{
                                            color: GOLD, fontFamily: MONO_FONT, fontSize: '0.65rem', fontWeight: 600,
                                        }}>
                                            GRID: {gridPeakKw.toFixed(0)}kW peak, {gridEnergyKwh.toFixed(0)}kWh
                                        </Typography>
                                    </Box>
                                )}
                                {/* Per-agent */}
                                {Object.entries(agentStates).map(([id, state]) => {
                                    const label = agentLabels[id] || AGENT_LABELS_DEFAULT[id] || id.slice(0, 4).toUpperCase();
                                    const ds = dailyStats[id] || {};
                                    // Use generation metrics for DER agents (site_dna)
                                    const peak = ds.generation_peak_kw != null ? ds.generation_peak_kw : (ds.peak_kw || 0);
                                    const energy = ds.generation_energy_kwh != null ? ds.generation_energy_kwh : (ds.energy_kwh || 0);
                                    const extras = [];
                                    if (state.soc_pct != null) extras.push(`SOC ${(state.soc_pct * 100).toFixed(0)}%`);
                                    if (state.vehicles_charging != null) extras.push(`${state.vehicles_charging}chg`);
                                    const extraStr = extras.length > 0 ? ` (${extras.join(', ')})` : '';

                                    return (
                                        <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{
                                                width: 6, height: 6, borderRadius: '50%',
                                                background: peak > 0 ? '#4caf50' : '#555',
                                                boxShadow: peak > 0 ? '0 0 4px #4caf50' : 'none',
                                            }} />
                                            <Typography sx={{
                                                color: 'rgba(244,241,236,0.7)', fontFamily: MONO_FONT, fontSize: '0.65rem',
                                            }}>
                                                {label}: {peak.toFixed(0)}kW peak, {energy.toFixed(0)}kWh{extraStr}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Console output area */}
                        <Box
                            ref={consoleRef}
                            sx={{
                                px: 2, py: 1.5,
                                minHeight: 120,
                                maxHeight: 280,
                                overflow: 'auto',
                                '&::-webkit-scrollbar': { width: 4 },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'rgba(178,140,80,0.3)', borderRadius: 2,
                                },
                            }}
                        >
                            {/* System messages during early phases (before activity log appears) */}
                            {activityLog.length === 0 && (
                                <>
                                    <ConsoleLine
                                        prefix="SYS"
                                        text="Initializing annual simulation..."
                                        color="rgba(244,241,236,0.5)"
                                    />
                                    {(phase === 'initializing' || phase === 'weather' || phase === 'tariff' || phase === 'siloed' || phase === 'orchestrated') && (
                                        <ConsoleLine
                                            prefix="SYS"
                                            text="Loading facility configuration from project..."
                                            color="rgba(244,241,236,0.5)"
                                        />
                                    )}
                                    {(phase === 'weather' || phase === 'tariff' || phase === 'siloed' || phase === 'orchestrated') && (
                                        <ConsoleLine
                                            prefix="WX"
                                            text="Fetching 8760 hourly temperatures from NREL PVWatts..."
                                            color="rgba(244,241,236,0.5)"
                                        />
                                    )}
                                    {(phase === 'tariff' || phase === 'siloed' || phase === 'orchestrated') && (
                                        <ConsoleLine
                                            prefix="TRF"
                                            text="Loading energy prices from tariff schedule..."
                                            color="rgba(244,241,236,0.5)"
                                        />
                                    )}
                                    {(phase === 'siloed' || phase === 'orchestrated') && (
                                        <ConsoleLine
                                            prefix="SIM"
                                            text="Starting 35,040 timestep simulation..."
                                            color={GOLD}
                                        />
                                    )}
                                    {/* Current phase message */}
                                    {progress.message && (
                                        <ConsoleLine
                                            prefix={PHASE_LABELS[phase]?.slice(0, 3) || 'SYS'}
                                            text={progress.message}
                                            color={GOLD}
                                            active
                                            typewriter
                                        />
                                    )}
                                </>
                            )}

                            {/* Real-time activity log from step callbacks */}
                            {activityLog.map((entry, i) => {
                                const isLast = i === activityLog.length - 1;
                                return (
                                    <ConsoleLine
                                        key={`${entry.phase}-${entry.day}`}
                                        prefix={`D${String(entry.day).padStart(3, '0')}`}
                                        text={remapLogText(entry.text)}
                                        color={entry.phase === 'orchestrated' ? GOLD_LIGHT : 'rgba(244,241,236,0.6)'}
                                        active={isLast}
                                        typewriter={isLast}
                                    />
                                );
                            })}
                        </Box>
                    </Paper>

                    {/* Live cost counters */}
                    {totals.siloedCost > 0 && (
                        <Paper sx={{ p: 2.5, mb: 3, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                            <Grid container spacing={3}>
                                <Grid item xs={4}>
                                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT }}>
                                        Siloed Cost
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT, fontWeight: 700 }}>
                                        ${totals.siloedCost.toLocaleString()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT }}>
                                        Orchestrated
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: GOLD, fontFamily: MONO_FONT, fontWeight: 700 }}>
                                        {totals.orchCost > 0 ? `$${totals.orchCost.toLocaleString()}` : '\u2014'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT }}>
                                        Savings
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: GOLD_LIGHT, fontFamily: MONO_FONT, fontWeight: 700 }}>
                                        {totals.savings > 0 ? `$${totals.savings.toLocaleString()} (${totals.pct}%)` : '\u2014'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Cumulative chart */}
                    {cumulativeData.length > 1 && (
                        <Paper sx={{ p: 2.5, mb: 3, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                            <Typography variant="subtitle2" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, mb: 1 }}>
                                Cumulative Cost
                            </Typography>
                            <ResponsiveContainer width="100%" height={200}>
                                <AreaChart data={cumulativeData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(178,140,80,0.1)" />
                                    <XAxis dataKey="month" tick={{ fill: TEXT_SECONDARY, fontSize: 11, fontFamily: MONO_FONT }} />
                                    <YAxis tick={{ fill: TEXT_SECONDARY, fontSize: 11, fontFamily: MONO_FONT }}
                                        tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                                    <Area type="monotone" dataKey="siloed" stroke="#888" fill="rgba(136,136,136,0.15)"
                                        strokeWidth={2} name="Siloed" />
                                    <Area type="monotone" dataKey="orchestrated" stroke={GOLD} fill="rgba(178,140,80,0.15)"
                                        strokeWidth={2} name="Orchestrated" connectNulls={false} />
                                    <RechartsTooltip
                                        contentStyle={{ background: CARD_BG, border: `1px solid ${GOLD_BORDER}`, fontFamily: MONO_FONT, fontSize: 12 }}
                                        formatter={(v) => v != null ? [`$${v.toLocaleString()}`, undefined] : ['\u2014', undefined]}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Paper>
                    )}

                    {/* Monthly bars */}
                    {barData.length > 0 && (
                        <Paper sx={{ p: 2.5, mb: 3, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                            <Typography variant="subtitle2" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, mb: 1 }}>
                                Monthly Cost
                            </Typography>
                            <ResponsiveContainer width="100%" height={160}>
                                <BarChart data={barData} barGap={2}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(178,140,80,0.1)" />
                                    <XAxis dataKey="month" tick={{ fill: TEXT_SECONDARY, fontSize: 10, fontFamily: MONO_FONT }} />
                                    <YAxis tick={{ fill: TEXT_SECONDARY, fontSize: 10, fontFamily: MONO_FONT }}
                                        tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                                    <Bar dataKey="siloed" fill="#666" radius={[2,2,0,0]} name="Siloed" />
                                    <Bar dataKey="orchestrated" fill={GOLD} radius={[2,2,0,0]} name="Orchestrated" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    )}

                    {/* Monthly savings feed */}
                    {monthlyFeed.length > 0 && (
                        <Paper sx={{
                            p: 2, background: isRetro ? '#1a1a1a' : '#f5f5f5', border: `1px solid ${GOLD_BORDER}`,
                            maxHeight: 200, overflow: 'auto',
                        }}>
                            <Typography variant="subtitle2" sx={{ color: GOLD, fontFamily: HEADING_FONT, mb: 1 }}>
                                Monthly Results
                            </Typography>
                            {monthlyFeed.map((line, i) => {
                                const isLast = i === monthlyFeed.length - 1;
                                return (
                                    <ConsoleLine
                                        key={`mfeed-${i}`}
                                        prefix="MO"
                                        text={line}
                                        color={line.includes('Savings') ? GOLD_LIGHT : 'rgba(244,241,236,0.6)'}
                                        active={isLast}
                                        typewriter={isLast}
                                    />
                                );
                            })}
                        </Paper>
                    )}
                </Grid>

                {/* Right column: agent network */}
                <Grid item xs={12} md={3}>
                    <Paper sx={{
                        p: 2, background: CARD_BG, border: `1px solid ${GOLD_BORDER}`,
                        display: 'flex', justifyContent: 'center',
                    }}>
                        <AgentNetworkDiagram
                            phase={networkPhase}
                            agents={agents}
                            coordinationIntensity={coordIntensity}
                            compact
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

/** Clean up agent names in log text.
 *  Backend now sends dynamic labels (e.g. "Solar+BESS+Gen") directly. */
function remapLogText(text) {
    return text
        .replace(/SITEDN/g, 'SITE')
        .replace(/BATTER/g, 'BATT')
        .replace(/EVFLEE/g, 'EV')
        .replace(/COLDST/g, 'COLD');
}

// ConsoleLine imported from @vortx/shared-ui — pass accentColor={GOLD} for EMS gold theme
